import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  archiveMessages,
  bulkFlag,
  bulkMarkSeen,
  clearSearch,
  clearSelection,
  deleteMessages,
  emptyFolder,
  folderLabel,
  loadMoreMessages,
  markFolderRead,
  notSpamMessages,
  openCompose,
  openMessage,
  refresh,
  runSearch,
  selectAll,
  selectedRefs,
  setListFilter,
  spamMessages,
  toggleFlagged,
  toggleSeen,
  toggleSelect,
} from "../actions";
import {
  activeList,
  currentMailbox,
  currentMailboxObj,
  drawerOpen,
  hasMoreMessages,
  isSearching,
  listError,
  listFilter,
  listLoading,
  loadingMore,
  mailboxes,
  messagesTotal,
  movePicker,
  refreshing,
  searchError,
  searchLoading,
  searchOptions,
  searchQuery,
  selectedMailbox,
  selectedUid,
  selectedUids,
} from "../state";
import { prefs } from "../prefs";
import { locale, t } from "../i18n";
import type { Envelope, ListFilter, SearchField, SearchOptions } from "../../shared/types";
import { dateGroup, displayName, formatFullDate, formatListDate, monthLabel, senderLabel, type DateGroup } from "../util";
import { Icon } from "./icons";
import { Avatar, DESKTOP, Dropdown, EmptyState, MenuItem, Segmented, Spinner, Toggle, useMedia } from "./kit";
import { DRAG_MIME, type DragPayload } from "./Sidebar";

export function MessageList(): JSX.Element {
  const listRef = useRef<HTMLDivElement | null>(null);
  const searching = isSearching.value;
  const items = activeList.value;
  const selCount = selectedUids.value.size;
  const loading = searching ? searchLoading.value : listLoading.value;
  const error = searching ? searchError.value : listError.value;

  // Back to the top when the folder or filter changes.
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [currentMailbox.value, listFilter.value, searching]);

  return (
    <section class="relative flex h-full min-h-0 flex-col" aria-label={t("list.label")}>
      <ComposeFab />
      <ListHeader />
      {selCount > 0 ? <BulkBar count={selCount} total={items.length} /> : !searching && <FilterBar />}

      <div
        ref={listRef}
        class="relative min-h-0 flex-1 overflow-y-auto overscroll-contain pb-24 md:pb-0"
        onScroll={(e) => {
          if (searching) return;
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 400) void loadMoreMessages();
        }}
        role="list"
      >
        {loading && items.length === 0 && <ListSkeleton />}
        {!loading && error && (
          <div class="m-4 flex items-start gap-3 rounded-[10px] border border-danger/30 bg-danger/5 p-4 text-[13px]">
            <Icon name="alert" size={18} class="mt-0.5 shrink-0 text-danger" />
            <div class="flex-1">
              <p class="font-medium text-fg">{t("list.loadError")}</p>
              <p class="mt-0.5 text-fg-3">{error}</p>
            </div>
            <button class="btn btn-secondary btn-sm" onClick={() => (searching ? void runSearch(searchQuery.value) : void refresh())}>
              {t("common.retry")}
            </button>
          </div>
        )}
        {!loading && !error && items.length === 0 && <ListEmpty />}
        {items.length > 0 && <Rows items={items} />}
        {!searching && hasMoreMessages.value && items.length > 0 && (
          <div class="flex justify-center py-4">
            {loadingMore.value ? (
              <Spinner />
            ) : (
              <button class="btn btn-ghost btn-sm" onClick={() => void loadMoreMessages()}>
                {t("list.loadMore")}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/** Phones have no sidebar on screen, so writing gets a floating button. */
function ComposeFab(): JSX.Element | null {
  const desktop = useMedia(DESKTOP);
  if (desktop || selectedUids.value.size > 0) return null;
  return (
    <button
      class="fixed bottom-5 right-5 z-30 flex h-14 items-center gap-2 rounded-[16px] bg-accent px-5 font-medium text-on-accent shadow-[var(--shadow-float)] active:scale-95"
      onClick={() => openCompose()}
      aria-label={t("compose.new")}
    >
      <Icon name="drafts" size={19} />
      {t("compose.new")}
    </button>
  );
}

/* --------------------------------- header -------------------------------- */

function ListHeader(): JSX.Element {
  const box = currentMailboxObj.value;
  const searching = isSearching.value;
  const title = searching ? t("search.results") : folderLabel(currentMailbox.value);
  const desktop = useMedia(DESKTOP);
  const total = messagesTotal.value;
  const unread = box?.unread ?? 0;
  const sub = searching
    ? searchLoading.value
      ? t("search.searching")
      : t("search.count", { n: activeList.value.length })
    : listFilter.value === "all"
      ? unread > 0 && box?.specialUse !== "sent"
        ? t("list.summaryUnread", { n: total, u: unread })
        : t("list.summary", { n: total })
      : t(listFilter.value === "unread" ? "list.summaryFilterUnread" : "list.summaryFilterStarred", { n: total });

  return (
    <header class="shrink-0 border-b border-line">
      <div class="flex h-14 items-center gap-2 px-3 md:px-4">
        {!desktop && (
          <button class="icon-btn -ml-1" onClick={() => (drawerOpen.value = true)} aria-label={t("nav.open")}>
            <Icon name="menu" size={20} />
          </button>
        )}
        <div class="min-w-0 flex-1">
          <h1 class="truncate text-[17px] font-semibold leading-tight tracking-tight text-fg">{title}</h1>
          <p class="truncate text-xs text-fg-3">{sub}</p>
        </div>
        <button
          class="icon-btn"
          title={t("list.refresh")}
          aria-label={t("list.refresh")}
          onClick={() => void refresh()}
          disabled={refreshing.value}
        >
          <Icon name="refresh" size={17} class={refreshing.value ? "animate-spin" : ""} />
        </button>
        {box && !searching && (
          <Dropdown
            trigger={(p) => (
              <button {...p} class="icon-btn" title={t("list.folderActions")} aria-label={t("list.folderActions")}>
                <Icon name="dotsV" size={17} />
              </button>
            )}
          >
            {() => (
              <>
                <MenuItem icon="mailOpen" label={t("nav.markAllRead")} disabled={unread === 0} onSelect={() => void markFolderRead(box.path)} />
                {(box.specialUse === "trash" || box.specialUse === "junk") && (
                  <MenuItem icon="trash" label={t("nav.emptyFolder")} danger disabled={total === 0} onSelect={() => void emptyFolder(box.path)} />
                )}
              </>
            )}
          </Dropdown>
        )}
      </div>
      <div class="px-3 pb-3 md:px-4">
        <SearchBox />
      </div>
    </header>
  );
}

let searchTimer: ReturnType<typeof setTimeout> | undefined;

function SearchBox(): JSX.Element {
  const [value, setValue] = useState(searchQuery.value);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const opts = searchOptions.value;
  const advancedCount = [opts.unread, opts.flagged, opts.since, opts.before, opts.allFolders, opts.field && opts.field !== "all"].filter(Boolean).length;

  // Keep the box in sync when search is cleared elsewhere (Esc, folder switch).
  useEffect(() => {
    if (!isSearching.value && !searchQuery.value) setValue("");
  }, [isSearching.value, searchQuery.value]);

  function submit(q: string, o: SearchOptions = opts): void {
    clearTimeout(searchTimer);
    void runSearch(q, o);
  }

  return (
    <div class="relative">
      <div class="relative flex items-center">
        <span class="pointer-events-none absolute left-3 text-fg-4">
          {searchLoading.value ? <Spinner size={14} /> : <Icon name="search" size={16} />}
        </span>
        <input
          data-search-input
          type="search"
          class="field !h-9 !rounded-[8px] !border-transparent !bg-raised pl-9 pr-16 focus:!border-accent focus:!bg-surface"
          placeholder={t("search.placeholder", { folder: folderLabel(currentMailbox.value) })}
          aria-label={t("search.label")}
          value={value}
          onInput={(e) => {
            const v = (e.currentTarget as HTMLInputElement).value;
            setValue(v);
            clearTimeout(searchTimer);
            searchTimer = setTimeout(() => void runSearch(v), 350);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit(value);
            if (e.key === "Escape") {
              e.stopPropagation();
              setValue("");
              clearSearch();
              (e.currentTarget as HTMLInputElement).blur();
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              (document.querySelector("[data-row]") as HTMLElement | null)?.focus();
            }
          }}
        />
        <div class="absolute right-1 flex items-center">
          {(value || isSearching.value) && (
            <button
              class="icon-btn icon-btn-sm"
              aria-label={t("search.clear")}
              onClick={() => {
                setValue("");
                clearSearch();
              }}
            >
              <Icon name="x" size={15} />
            </button>
          )}
          <button
            class={`icon-btn icon-btn-sm relative ${showAdvanced || advancedCount ? "text-accent-fg" : ""}`}
            aria-label={t("search.options")}
            title={t("search.options")}
            aria-expanded={showAdvanced}
            data-adv-toggle
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <Icon name="sliders" size={15} />
            {advancedCount > 0 && <span class="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-accent" />}
          </button>
        </div>
      </div>
      {showAdvanced && (
        <AdvancedSearch
          initial={opts}
          onClose={() => setShowAdvanced(false)}
          onApply={(o) => {
            setShowAdvanced(false);
            submit(value, o);
          }}
        />
      )}
    </div>
  );
}

function AdvancedSearch({
  initial,
  onApply,
  onClose,
}: {
  initial: SearchOptions;
  onApply: (o: SearchOptions) => void;
  onClose: () => void;
}): JSX.Element {
  const [o, setO] = useState<SearchOptions>({ field: "all", ...initial });
  const ref = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      // The toggle button handles its own clicks; the search input keeps it open.
      if (ref.current?.contains(target) || target.closest("[data-adv-toggle], [data-search-input]")) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  const set = (patch: Partial<SearchOptions>): void => setO({ ...o, ...patch });

  return (
    <form
      ref={ref}
      class="float-panel animate-fade absolute inset-x-0 top-full z-30 mt-1.5 space-y-4 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onApply(o);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div>
        <span class="field-label">{t("search.lookIn")}</span>
        <div class="overflow-x-auto">
          <Segmented<SearchField>
            size="sm"
            label={t("search.lookIn")}
            value={o.field ?? "all"}
            onChange={(field) => set({ field })}
            options={[
              { value: "all", label: t("search.field.all") },
              { value: "from", label: t("search.field.from") },
              { value: "to", label: t("search.field.to") },
              { value: "subject", label: t("search.field.subject") },
              { value: "body", label: t("search.field.body") },
            ]}
          />
        </div>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <label>
          <span class="field-label">{t("search.since")}</span>
          <input type="date" class="field" value={o.since ?? ""} onInput={(e) => set({ since: (e.currentTarget as HTMLInputElement).value || undefined })} />
        </label>
        <label>
          <span class="field-label">{t("search.before")}</span>
          <input type="date" class="field" value={o.before ?? ""} onInput={(e) => set({ before: (e.currentTarget as HTMLInputElement).value || undefined })} />
        </label>
      </div>
      <div class="space-y-2.5">
        {(
          [
            ["unread", t("search.onlyUnread")],
            ["flagged", t("search.onlyStarred")],
            ["allFolders", t("search.allFolders")],
          ] as const
        ).map(([key, label]) => (
          <label key={key} class="flex items-center justify-between gap-3 text-[13px] text-fg-2">
            {label}
            <Toggle label={label} checked={Boolean(o[key])} onChange={(v) => set({ [key]: v || undefined })} />
          </label>
        ))}
      </div>
      <div class="flex justify-between gap-2 border-t border-line pt-3">
        <button type="button" class="btn btn-ghost btn-sm" onClick={() => setO({ field: "all" })}>
          {t("search.reset")}
        </button>
        <button type="submit" class="btn btn-primary btn-sm">
          {t("search.apply")}
        </button>
      </div>
    </form>
  );
}

function FilterBar(): JSX.Element {
  const items = activeList.value;
  return (
    <div class="flex h-11 shrink-0 items-center gap-2 border-b border-line px-3 md:px-4">
      <input
        type="checkbox"
        class="h-4 w-4 shrink-0 cursor-pointer rounded accent-[var(--color-accent)]"
        aria-label={t("list.selectAll")}
        title={t("list.selectAll")}
        checked={false}
        disabled={items.length === 0}
        onChange={() => selectAll()}
      />
      <Segmented<ListFilter>
        size="sm"
        label={t("list.filter")}
        value={listFilter.value}
        onChange={(f) => void setListFilter(f)}
        options={[
          { value: "all", label: t("list.filter.all") },
          { value: "unread", label: t("list.filter.unread") },
          { value: "flagged", label: t("list.filter.starred") },
        ]}
      />
    </div>
  );
}

function BulkBar({ count, total }: { count: number; total: number }): JSX.Element {
  const use = currentMailboxObj.value?.specialUse;
  const refs = (): ReturnType<typeof selectedRefs> => selectedRefs();
  const anyUnread = activeList.value.some((m) => selectedUids.value.has(m.uid) && !m.seen);
  const allSelected = count === total;
  return (
    <div class="flex h-11 shrink-0 items-center gap-1 border-b border-line bg-accent/6 px-3 md:px-4">
      <input
        type="checkbox"
        class="h-4 w-4 shrink-0 cursor-pointer rounded accent-[var(--color-accent)]"
        aria-label={allSelected ? t("list.clearSelection") : t("list.selectAll")}
        checked={allSelected}
        ref={(el: HTMLInputElement | null) => {
          if (el) el.indeterminate = !allSelected;
        }}
        onChange={() => (allSelected ? clearSelection() : selectAll())}
      />
      <span class="ml-1.5 mr-auto text-[13px] font-medium text-fg">{t("list.selected", { n: count })}</span>
      {use !== "archive" && (
        <BarButton icon="archive" label={t("action.archive")} onClick={() => void archiveMessages(refs())} />
      )}
      {use === "junk" ? (
        <BarButton icon="inbox" label={t("action.notSpam")} onClick={() => void notSpamMessages(refs())} />
      ) : (
        <BarButton icon="spam" label={t("action.spam")} onClick={() => void spamMessages(refs())} />
      )}
      <BarButton icon="trash" label={t("action.delete")} onClick={() => void deleteMessages(refs())} />
      <BarButton
        icon="folderMove"
        label={t("action.move")}
        onClick={() => (movePicker.value = { uids: [...selectedUids.value], mailbox: currentMailbox.value })}
      />
      <Dropdown
        trigger={(p) => (
          <button {...p} class="icon-btn" title={t("common.more")} aria-label={t("common.more")}>
            <Icon name="dotsV" size={17} />
          </button>
        )}
      >
        {() => (
          <>
            <MenuItem icon="mailOpen" label={t("action.markRead")} onSelect={() => void bulkMarkSeen(true)} disabled={!anyUnread} />
            <MenuItem icon="mailUnread" label={t("action.markUnread")} onSelect={() => void bulkMarkSeen(false)} />
            <MenuItem icon="star" label={t("action.star")} onSelect={() => void bulkFlag(true)} />
            <MenuItem icon="star" label={t("action.unstar")} onSelect={() => void bulkFlag(false)} />
            <div class="menu-sep" />
            <MenuItem icon="x" label={t("list.clearSelection")} onSelect={() => clearSelection()} />
          </>
        )}
      </Dropdown>
    </div>
  );
}

function BarButton({ icon, label, onClick }: { icon: Parameters<typeof Icon>[0]["name"]; label: string; onClick: () => void }): JSX.Element {
  return (
    <button class="icon-btn" title={label} aria-label={label} onClick={onClick}>
      <Icon name={icon} size={17} />
    </button>
  );
}

/* ---------------------------------- rows --------------------------------- */

function groupTitle(group: DateGroup, iso: string): string {
  if (group === "older") return monthLabel(iso, locale.value);
  return t(`date.${group}` as const);
}

function Rows({ items }: { items: Envelope[] }): JSX.Element {
  const out: JSX.Element[] = [];
  let lastKey = "";
  const now = new Date();
  for (const m of items) {
    const g = dateGroup(m.date, now);
    if (g.key !== lastKey) {
      lastKey = g.key;
      out.push(
        <div
          key={`g:${g.key}`}
          class="sticky top-0 z-10 border-b border-line bg-surface/95 px-4 py-1.5 text-xs font-medium text-fg-3 backdrop-blur"
          role="presentation"
        >
          {groupTitle(g.group, m.date)}
        </div>,
      );
    }
    out.push(<Row key={`${m.mailbox}:${m.uid}`} m={m} />);
  }
  return <>{out}</>;
}

function Row({ m }: { m: Envelope }): JSX.Element {
  const openMbox = selectedMailbox.value ?? currentMailbox.value;
  const active = selectedUid.value === m.uid && openMbox === m.mailbox;
  const checked = selectedUids.value.has(m.uid);
  const selecting = selectedUids.value.size > 0;
  const box = mailboxes.value.find((b) => b.path === m.mailbox);
  const outgoing = box?.specialUse === "sent" || box?.specialUse === "drafts";
  const who = outgoing
    ? m.to.length
      ? `${t("list.toPrefix")} ${m.to.map((a) => displayName(a)).join(", ")}`
      : t("list.noRecipients")
    : senderLabel(m);
  const avatarAddr = outgoing ? (m.to[0]?.address ?? "") : (m.from[0]?.address ?? "");
  const p = prefs.value;
  const compact = p.density === "compact";
  const crossFolder = m.mailbox !== currentMailbox.value;
  const ref = { uid: m.uid, mailbox: m.mailbox };
  const swipe = useSwipe(
    () => void archiveMessages([ref]),
    () => void toggleSeen(m.uid, m.mailbox),
  );

  function activate(e: MouseEvent | KeyboardEvent): void {
    if (e.shiftKey) {
      toggleSelect(m.uid, true);
      return;
    }
    if (e.ctrlKey || e.metaKey || selecting) {
      toggleSelect(m.uid);
      return;
    }
    void openMessage(m.uid, m.mailbox);
  }

  return (
    <div class="relative overflow-hidden" role="listitem">
      {/* Swipe affordances (phones). */}
      {swipe.dx !== 0 && (
        <div class={`absolute inset-0 flex items-center px-6 text-on-accent ${swipe.dx < 0 ? "justify-end bg-accent" : "justify-start bg-fg-3"}`}>
          <Icon name={swipe.dx < 0 ? "archive" : m.seen ? "mailUnread" : "mailOpen"} size={20} />
        </div>
      )}
      <div
        data-row
        data-uid={String(m.uid)}
        tabIndex={0}
        role="button"
        aria-current={active ? "true" : undefined}
        aria-label={`${who}: ${m.subject || t("common.noSubject")}${m.seen ? "" : `, ${t("list.unread")}`}`}
        draggable
        onDragStart={(e) => {
          const uids = checked ? [...selectedUids.value] : [m.uid];
          const payload: DragPayload = { mailbox: m.mailbox, uids };
          e.dataTransfer!.effectAllowed = "move";
          e.dataTransfer!.setData(DRAG_MIME, JSON.stringify(payload));
          e.dataTransfer!.setData("text/plain", m.subject);
        }}
        onClick={activate}
        onKeyDown={(e) => {
          if (e.key === "Enter") activate(e);
          if (e.key === " ") {
            e.preventDefault();
            toggleSelect(m.uid, e.shiftKey);
          }
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const rows = [...document.querySelectorAll<HTMLElement>("[data-row]")];
            const i = rows.indexOf(e.currentTarget as HTMLElement);
            rows[i + (e.key === "ArrowDown" ? 1 : -1)]?.focus();
          }
        }}
        {...swipe.handlers}
        style={swipe.dx ? `transform:translateX(${swipe.dx}px)` : undefined}
        class={`mail-row group relative flex cursor-default gap-3 border-b border-line pl-4 pr-3 outline-none transition-colors focus-visible:bg-raised ${
          active ? "bg-accent/8" : checked ? "bg-accent/5" : "bg-surface hover:bg-raised/60"
        } ${!m.seen ? "unread-spine" : ""} ${swipe.dx ? "" : "transition-transform"}`}
      >
        {/* Avatar doubles as the selection toggle. */}
        <button
          type="button"
          class="relative mt-0.5 h-9 w-9 shrink-0 self-start rounded-full"
          aria-label={checked ? t("list.deselect") : t("list.select")}
          aria-pressed={checked}
          onClick={(e) => {
            e.stopPropagation();
            toggleSelect(m.uid, e.shiftKey);
          }}
        >
          {checked || selecting ? (
            <span
              class={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                checked ? "border-accent bg-accent text-on-accent" : "border-line-strong bg-surface text-transparent hover:border-accent"
              }`}
            >
              <Icon name="check" size={16} strokeWidth={2.5} />
            </span>
          ) : (
            <>
              <span class="group-hover:hidden">
                <Avatar address={avatarAddr} label={who.replace(`${t("list.toPrefix")} `, "")} size={36} />
              </span>
              <span class="hidden h-9 w-9 items-center justify-center rounded-full border-2 border-line-strong bg-surface text-transparent hover:border-accent group-hover:flex">
                <Icon name="check" size={16} strokeWidth={2.5} />
              </span>
            </>
          )}
        </button>

        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-2">
            <span class={`min-w-0 flex-1 truncate text-[13.5px] ${m.seen ? "text-fg-2" : "font-semibold text-fg"}`}>
              {m.draft && <span class="mr-1 font-medium text-danger">{t("list.draftTag")}</span>}
              {who}
            </span>
            <time
              class={`shrink-0 text-xs tabular-nums group-hover:invisible ${m.seen ? "text-fg-3" : "font-semibold text-accent-fg"}`}
              title={formatFullDate(m.date, locale.value)}
              dateTime={m.date}
            >
              {formatListDate(m.date, locale.value, t("date.yesterday"))}
            </time>
          </div>
          <div class="mt-[var(--row-gap)] flex items-center gap-1.5">
            <span class={`min-w-0 flex-1 truncate text-[13px] ${m.seen ? "text-fg-3" : "font-medium text-fg"}`}>
              {m.subject || t("common.noSubject")}
              {compact && p.showPreviews && m.preview && <span class="font-normal text-fg-4"> — {m.preview}</span>}
            </span>
            {m.answered && <Icon name="reply" size={13} class="shrink-0 text-fg-4" />}
            {m.hasAttachments && <Icon name="paperclip" size={13} class="shrink-0 text-fg-3" />}
            {m.flagged && <Icon name="starFilled" size={14} class="shrink-0 text-star" />}
          </div>
          {!compact && p.showPreviews && m.preview && (
            <p class="mt-[var(--row-gap)] line-clamp-1 text-[12.5px] leading-snug text-fg-3">{m.preview}</p>
          )}
          {crossFolder && (
            <span class="chip mt-1.5 !h-5 !text-[11px]">
              <Icon name="folder" size={11} />
              {folderLabel(m.mailbox)}
            </span>
          )}
        </div>

        <RowActions m={m} />
      </div>
    </div>
  );
}

/** Hover actions; they sit where the date is, so the row never reflows. */
function RowActions({ m }: { m: Envelope }): JSX.Element {
  const ref = { uid: m.uid, mailbox: m.mailbox };
  const use = mailboxes.value.find((b) => b.path === m.mailbox)?.specialUse;
  const stop = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };
  return (
    <div class="absolute right-2 top-[calc(var(--row-py)-4px)] hidden items-center rounded-[7px] border border-line bg-surface shadow-sm group-hover:flex">
      {use !== "archive" && (
        <button class="icon-btn icon-btn-sm" title={t("action.archive")} aria-label={t("action.archive")} onClick={stop(() => void archiveMessages([ref]))}>
          <Icon name="archive" size={15} />
        </button>
      )}
      <button class="icon-btn icon-btn-sm" title={t("action.delete")} aria-label={t("action.delete")} onClick={stop(() => void deleteMessages([ref]))}>
        <Icon name="trash" size={15} />
      </button>
      <button
        class="icon-btn icon-btn-sm"
        title={m.seen ? t("action.markUnread") : t("action.markRead")}
        aria-label={m.seen ? t("action.markUnread") : t("action.markRead")}
        onClick={stop(() => void toggleSeen(m.uid, m.mailbox))}
      >
        <Icon name={m.seen ? "mailUnread" : "mailOpen"} size={15} />
      </button>
      <button
        class="icon-btn icon-btn-sm"
        title={m.flagged ? t("action.unstar") : t("action.star")}
        aria-label={m.flagged ? t("action.unstar") : t("action.star")}
        onClick={stop(() => void toggleFlagged(m.uid, m.mailbox))}
      >
        <Icon name={m.flagged ? "starFilled" : "star"} size={15} class={m.flagged ? "text-star" : ""} />
      </button>
    </div>
  );
}

/** Horizontal swipe on touch: left = archive, right = toggle read. */
function useSwipe(onLeft: () => void, onRight: () => void): {
  dx: number;
  handlers: Record<string, (e: PointerEvent) => void>;
} {
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number; locked: boolean | null } | null>(null);
  const THRESHOLD = 96;
  return {
    dx,
    handlers: {
      onPointerDown: (e) => {
        if (e.pointerType !== "touch") return;
        start.current = { x: e.clientX, y: e.clientY, locked: null };
      },
      onPointerMove: (e) => {
        const s = start.current;
        if (!s) return;
        const ddx = e.clientX - s.x;
        const ddy = e.clientY - s.y;
        // Decide once whether this gesture is a horizontal swipe or a scroll.
        if (s.locked === null && (Math.abs(ddx) > 8 || Math.abs(ddy) > 8)) s.locked = Math.abs(ddx) > Math.abs(ddy) * 1.5;
        if (s.locked) setDx(Math.max(-160, Math.min(160, ddx)));
      },
      onPointerUp: () => {
        const d = dx;
        start.current = null;
        setDx(0);
        if (d <= -THRESHOLD) onLeft();
        else if (d >= THRESHOLD) onRight();
      },
      onPointerCancel: () => {
        start.current = null;
        setDx(0);
      },
    },
  };
}

function ListEmpty(): JSX.Element {
  if (isSearching.value) {
    return <EmptyState icon="search" title={t("search.noneTitle")} body={t("search.noneBody")} />;
  }
  const use = currentMailboxObj.value?.specialUse;
  if (listFilter.value !== "all") {
    return (
      <EmptyState
        icon={listFilter.value === "unread" ? "checkCircle" : "star"}
        title={listFilter.value === "unread" ? t("empty.unreadTitle") : t("empty.starredTitle")}
        body={listFilter.value === "unread" ? t("empty.unreadBody") : t("empty.starredBody")}
        action={
          <button class="btn btn-secondary btn-sm" onClick={() => void setListFilter("all")}>
            {t("empty.showAll")}
          </button>
        }
      />
    );
  }
  switch (use) {
    case "inbox":
      return <EmptyState icon="checkCircle" title={t("empty.inboxTitle")} body={t("empty.inboxBody")} />;
    case "drafts":
      return (
        <EmptyState
          icon="drafts"
          title={t("empty.draftsTitle")}
          body={t("empty.draftsBody")}
          action={
            <button class="btn btn-primary btn-sm" onClick={() => openCompose()}>
              {t("compose.new")}
            </button>
          }
        />
      );
    case "sent":
      return <EmptyState icon="sent" title={t("empty.sentTitle")} body={t("empty.sentBody")} />;
    case "trash":
      return <EmptyState icon="trash" title={t("empty.trashTitle")} body={t("empty.trashBody")} />;
    case "junk":
      return <EmptyState icon="spam" title={t("empty.spamTitle")} body={t("empty.spamBody")} />;
    default:
      return <EmptyState icon="folder" title={t("empty.folderTitle")} body={t("empty.folderBody")} />;
  }
}

function ListSkeleton(): JSX.Element {
  return (
    <div aria-hidden="true">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} class="flex gap-3 border-b border-line px-4 py-3.5">
          <span class="skeleton h-9 w-9 shrink-0 !rounded-full" />
          <span class="flex-1 space-y-2 pt-0.5">
            <span class="flex justify-between gap-6">
              <span class="skeleton block h-3 w-1/3" />
              <span class="skeleton block h-3 w-10" />
            </span>
            <span class="skeleton block h-3 w-3/4" />
            <span class="skeleton block h-3 w-1/2 opacity-60" />
          </span>
        </div>
      ))}
    </div>
  );
}
